/**
 * DriftValueDiff — what changed inside one drifted property, from
 * `diffDriftValues`: entries added to or removed from a list, fields that
 * changed, a list that only changed order. Added entries take the
 * high-contrast tag and removed ones the baseline tag (DESIGN.md, the Neutral
 * Bad State Rule); a changed field reads baseline tag, arrow, high-contrast tag.
 *
 * `full` is the caller's own two-value view (its labels differ by surface).
 * It renders in place of the diff when the diff adds nothing (one plain value
 * swapped for another), and behind a toggle otherwise.
 */

import { useState, type ReactNode } from 'react'
import { Button } from '@/components/carbon/Button'
import { DiffList, type DiffRow } from '@/components/carbon/DiffList'
import { Tag } from '@/components/carbon/Tag'
import { ChangeCell } from '@/components/BaselineCompliance/drift-carbon'
import { diffDriftValues, formatDiffLeaf, isPlainValueChange, type ValueDiffGroup } from '@/lib/drift-value-diff'

interface DriftValueDiffProps {
  expected: unknown
  actual: unknown
  full: ReactNode
}

export function DriftValueDiff({ expected, actual, full }: DriftValueDiffProps) {
  const [showFull, setShowFull] = useState(false)
  const groups = diffDriftValues(expected, actual)

  if (isPlainValueChange(groups)) return <>{full}</>

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {groups.length === 0 ? (
        <p className="m-0 text-helper text-text-helper">
          Both values hold the same entries once read. Only the formatting differs.
        </p>
      ) : (
        groups.map(g => <DiffGroup key={g.path.join('\u0000')} group={g} />)
      )}
      <div>
        <Button kind="ghost" size="sm" onClick={() => setShowFull(v => !v)} aria-expanded={showFull}>
          {showFull ? 'Hide both values' : 'Show both values in full'}
        </Button>
      </div>
      {showFull ? full : null}
    </div>
  )
}

function DiffGroup({ group }: { group: ValueDiffGroup }) {
  const rows: DiffRow[] = []
  if (group.added.length > 0) rows.push({ label: 'Added', value: <LeafTags values={group.added} now /> })
  if (group.removed.length > 0) rows.push({ label: 'Removed', value: <LeafTags values={group.removed} /> })
  if (group.change) {
    rows.push({
      label: 'Changed',
      value: <ChangeCell from={formatDiffLeaf(group.change.from)} to={formatDiffLeaf(group.change.to)} />,
    })
  }
  if (group.reordered) {
    rows.push({ label: 'Order', value: <span className="text-text-secondary">Same entries in a new order.</span> })
  }

  return (
    <div className="min-w-0">
      {group.path.length > 0 ? (
        <p className="m-0 mb-2 font-mono text-helper text-text-secondary [overflow-wrap:anywhere]">
          {group.path.join(' › ')}
        </p>
      ) : null}
      <DiffList rows={rows} />
    </div>
  )
}

function LeafTags({ values, now = false }: { values: unknown[]; now?: boolean }) {
  return (
    <span className="flex max-h-40 min-w-0 flex-wrap gap-1 overflow-auto">
      {values.map((v, i) => {
        const text = formatDiffLeaf(v)
        return (
          <span key={`${text}-${i}`} className="flex min-w-0 max-w-[240px]">
            {now ? (
              <Tag kind="high-contrast" title={text}>
                {text}
              </Tag>
            ) : (
              <Tag title={text} className="text-text-secondary">
                {text}
              </Tag>
            )}
          </span>
        )
      })}
    </span>
  )
}

export default DriftValueDiff
